import { ChangeEvent, FC, ReactNode } from "react"
import { useSelector } from "react-redux"
import FormSent from "./FormSent"
import Button, { ButtonIconPosition } from "@/components/shared/Button"
import { Icon, IconColor } from "@/components/shared/Icon"
import TextArea from "@/components/shared/TextArea"
import TextInput from "@/components/shared/TextInput"
import { TestId } from "@/constants/globals"
import useStateHandler from "@/hooks/useStateHandler"
import { RootState } from "@/store"
import { isEmpty } from "@/utils/lodash"

export type ClearAlertForm = {
   name: string
   reportNote: string
}

interface FormProps {
   loading: boolean
   isSuccess: boolean
   title: string
   description: string
   reportNotePlaceholder: string
   buttonText: string
   successTitle: string
   successDescription: ReactNode
   errorMessage: string | null
   onClick: (form: ClearAlertForm) => void
}

const Form: FC<FormProps> = ({
   loading,
   isSuccess,
   title,
   description,
   reportNotePlaceholder,
   buttonText,
   successTitle,
   successDescription,
   errorMessage,
   onClick,
}) => {
   const { firstName, lastName } = useSelector(({ userReducer }: RootState) => {
      return userReducer
   })
   const fullName = [firstName, lastName].join(" ")

   const [state, setState] = useStateHandler<ClearAlertForm>({
      name: fullName,
      reportNote: "",
   })
   const { name, reportNote } = state
   const isFormValid = !loading && !isEmpty(name) && !isEmpty(reportNote)

   const handleChange = (
      e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
   ) => {
      setState(e)
   }

   if (isSuccess) {
      return (
         <FormSent
            title={successTitle}
            description={successDescription}
         />
      )
   }

   return (
      <>
         <div className="clear-alert-modal-form__header">
            <div className="clear-alert-modal-form__header-title">
               <Icon
                  size="24"
                  name="shield"
                  color={IconColor.HONOLULU_BLUE}
               />
               <span>{title}</span>
            </div>
            <div className="clear-alert-modal-form__header-description">
               <span>{description}</span>
            </div>
         </div>
         <div className="clear-alert-modal-form__inputs">
            <TextInput
               name="name"
               placeholder="Your name"
               value={name}
               onChange={handleChange}
               testId={TestId.CLEAR_ALERT_MODAL_NAME_INPUT}
            />
            <TextArea
               name="reportNote"
               placeholder={reportNotePlaceholder}
               value={reportNote}
               onChange={handleChange}
               data-testid={TestId.CLEAR_ALERT_MODAL_REPORT_NOTE_INPUT}
            />
         </div>
         <Button
            disabled={!isFormValid}
            iconName="checkmark"
            iconPosition={ButtonIconPosition.LEFT}
            onClick={() => onClick({ name, reportNote })}
            data-testid={TestId.CLEAR_ALERT_MODAL_CLEAR_BUTTON}
         >
            {buttonText}
         </Button>
         <div className="clear-alert-modal-form__error">{errorMessage}</div>
      </>
   )
}

export default Form
